/**
 * Add-on Service (user-facing)
 *
 * Handles the public add-on catalog, purchases, payment and cancellation.
 * Admin add-on management lives in adminService.ts.
 */

import { apiRequest } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';
import type {
  Addon,
  AddonPurchase,
  AddonFamily,
  AddonAudience,
  AddonFulfillment,
  AddonPurchaseStatus,
} from './adminService';

// Re-export the shared types so pages have a single source of truth.
export type {
  Addon,
  AddonPurchase,
  AddonFamily,
  AddonAudience,
  AddonFulfillment,
  AddonPurchaseStatus,
};

/**
 * Get the auth token from the persisted session.
 */
function getToken(): string {
  return getAuthToken();
}

export interface AddonPurchasesMeta {
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Fetch the active add-on catalog, optionally filtered by family / audience.
 */
export async function fetchAddons(params?: {
  family?: string;
  audience?: string;
}): Promise<Addon[]> {
  const query = new URLSearchParams();
  if (params?.family) query.set('family', params.family);
  if (params?.audience) query.set('audience', params.audience);
  const qs = query.toString();

  // Send the token (when signed in) so the backend's optionalAuth gate returns
  // the viewer's OWNER/BROKER/COMPANY add-ons, not just the anonymous ALL set.
  // Without it a logged-in FAL owner is treated as anonymous and sees no
  // property add-ons.
  const response = await apiRequest<{ data: Addon[] }>(
    `/addons${qs ? `?${qs}` : ''}`,
    { method: 'GET', token: getToken() || undefined },
  );
  return response.data;
}

/**
 * Fetch a single add-on by its slug.
 */
export async function fetchAddon(slug: string): Promise<Addon> {
  const response = await apiRequest<{ data: Addon }>(`/addons/${slug}`, {
    method: 'GET',
  });
  return response.data;
}

/**
 * Fetch the current user's add-on purchases.
 */
export async function fetchMyAddonPurchases(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
}): Promise<{ data: AddonPurchase[]; meta: AddonPurchasesMeta }> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  if (params?.status) query.set('status', params.status);
  const qs = query.toString();

  return await apiRequest<{ data: AddonPurchase[]; meta: AddonPurchasesMeta }>(
    `/addons/my-purchases${qs ? `?${qs}` : ''}`,
    { method: 'GET', token: getToken() },
  );
}

/**
 * Purchase (or request a quote for) an add-on.
 */
export async function purchaseAddon(
  slug: string,
  body: {
    propertyId?: string;
    durationDays?: number;
    notes?: string;
    /** Pay with coins (instant ACTIVE) instead of the default SAR flow. */
    pricingMethod?: AddonPurchase['pricingMethod'];
  },
): Promise<{ data: AddonPurchase; pricing: unknown }> {
  return await apiRequest<{ data: AddonPurchase; pricing: unknown }>(
    `/addons/${slug}/purchase`,
    { method: 'POST', token: getToken(), body },
  );
}

/**
 * Pay for (and instantly activate) a pending add-on purchase.
 */
export async function payAddonPurchase(id: string): Promise<AddonPurchase> {
  const response = await apiRequest<{ data: AddonPurchase }>(
    `/addons/purchases/${id}/pay`,
    { method: 'POST', token: getToken() },
  );
  return response.data;
}

/**
 * Cancel a pending add-on purchase.
 */
export async function cancelAddonPurchase(id: string): Promise<void> {
  await apiRequest<void>(`/addons/purchases/${id}`, {
    method: 'DELETE',
    token: getToken(),
  });
}

/**
 * Fetch the current user's plan add-on discount percentage.
 *
 * The backend derives this from the buyer's active subscription plan (reusing
 * the plan's boost discount). Returns 0 when the user has no active plan, is not
 * signed in, or on any error.
 */
export async function fetchMyAddonDiscount(): Promise<number> {
  try {
    const token = getToken();
    if (!token) return 0;
    const response = await apiRequest<{ data: { discountPercent: number } }>(
      '/addons/my-discount',
      { method: 'GET', token },
    );
    return response.data.discountPercent;
  } catch (err) {
    console.error('Failed to fetch add-on discount:', err);
    return 0;
  }
}

/** A listing the signed-in user may apply a property add-on to. */
export interface ApplicableProperty {
  id: string;
  title: string;
  titleAr?: string | null;
  status: string;
  city?: { nameEn: string; nameAr: string } | null;
  images?: { url: string; thumbnailUrl?: string | null }[];
}

/**
 * The viewer's own listings (owner OR the agent behind them) that a property
 * add-on can be applied to. Excludes drafts/internal listings.
 */
export async function fetchApplicableProperties(): Promise<ApplicableProperty[]> {
  const response = await apiRequest<{ data: ApplicableProperty[] }>(
    '/addons/applicable-properties',
    { method: 'GET', token: getToken() },
  );
  return response.data;
}

/**
 * Attach an owned, unapplied property add-on to one of the user's listings
 * (starts its clock + runs the effect). Not for account-scoped add-ons.
 */
export async function applyAddonPurchase(id: string, propertyId: string): Promise<AddonPurchase> {
  const response = await apiRequest<{ data: AddonPurchase }>(
    `/addons/purchases/${id}/apply`,
    { method: 'POST', token: getToken(), body: { propertyId } },
  );
  return response.data;
}

const addonService = {
  fetchAddons,
  fetchAddon,
  fetchMyAddonPurchases,
  purchaseAddon,
  payAddonPurchase,
  cancelAddonPurchase,
  fetchMyAddonDiscount,
  fetchApplicableProperties,
  applyAddonPurchase,
};

export default addonService;
